import {
  Box,
  Flex,
  HStack,
  Heading,
  Image,
  Button,
  ButtonGroup,
  IconButton,
  useColorModeValue,
  useColorMode,
} from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import { Icon } from './Icon'
import { SettingsButton } from './SettingsDrawer'
import AddressButton from './AddressButton'
import { useProjects, useSacrificeStats, useViewMode } from '../contexts'
import { useCallback } from 'react'
import { buildViewModeColorScheme, isAdminRole } from './NavigationBar.helpers'
import { useInstanceAdmin } from '../hooks/useInstanceAdmin'

export interface BottomNavProps {
  address: string | undefined
  isConnected: boolean
  isSignedIn: boolean
  onLogout?: () => void
}

export const BottomNav = ({ address, isConnected, isSignedIn, onLogout }: BottomNavProps) => {
  const { viewMode, setViewMode } = useViewMode()
  const { colorMode, toggleColorMode } = useColorMode()
  const bgColor = 'bg.band'
  const borderColor = 'line.dark'
  const { projectId } = useProjects()
  const { data } = useSacrificeStats()
  const lightMode = useColorModeValue(true, false)
  // Check if user has admin role - only show if signed in and has admin role
  const isAdmin = isAdminRole(isSignedIn, data?.user?.role?.role)
  const { isInstanceAdmin } = useInstanceAdmin()

  const colorSchemeProps = useCallback(
    (matchingMode: string, variant: string) =>
      buildViewModeColorScheme({ matchingMode, viewMode, variant, lightMode }),
    [lightMode, viewMode]
  )

  return (
    <Box
      as="nav"
      bg={bgColor}
      borderTopWidth="1px"
      borderTopStyle="solid"
      borderColor={borderColor}
      position="fixed"
      bottom="0"
      left="0"
      right={2}
      // width="100%"
      zIndex={1000}
      display={{ base: 'block', lg: 'none' }}
      sx={{
        bottom: 'max(0px, env(safe-area-inset-bottom))',
        paddingBottom: 'max(0px, env(safe-area-inset-bottom))',
      }}
    >
      <Flex align="center" justify="space-around" px={4} py={2.5} maxW="100vw" mx="auto" gap={1}>
        {/* Campaign directory link */}
        <IconButton
          as={RouterLink}
          to="/sacrifices"
          aria-label="All campaigns"
          variant="ghost"
          size="md"
          flexShrink={0}
          icon={<Icon icon="mdi:view-grid" height={24} width={24} />}
        />

        {/* Color mode toggle */}
        <IconButton
          aria-label="Toggle color mode"
          onClick={toggleColorMode}
          variant="ghost"
          size="md"
          flexShrink={0}
          icon={
            <Icon
              icon={colorMode === 'light' ? 'mdi:weather-night' : 'mdi:weather-sunny'}
              height={24}
              width={24}
            />
          }
        />

        {/* Settings */}
        <SettingsButton />

        {/* Instance dashboard — only visible to instance admins */}
        {isInstanceAdmin && (
          <IconButton
            as={RouterLink}
            to="/instance"
            aria-label="Instance"
            variant="ghost"
            size="md"
            flexShrink={0}
            icon={<Icon icon="mdi:shield-account" height={24} width={24} />}
          />
        )}

        {/* View mode buttons */}
        {projectId && (
          <>
            <IconButton
              {...colorSchemeProps('global', 'green')}
              aria-label={viewMode === 'global' ? 'Global view (current)' : 'Switch to global view'}
              aria-pressed={viewMode === 'global'}
              onClick={() => setViewMode('global')}
              size="md"
              flexShrink={0}
              icon={<Icon icon="game-icons:rank-3" height={24} width={24} />}
            />
            <IconButton
              {...colorSchemeProps('user', 'green')}
              aria-label={viewMode === 'user' ? 'User view (current)' : 'Switch to user view'}
              aria-pressed={viewMode === 'user'}
              onClick={() => setViewMode('user')}
              size="md"
              flexShrink={0}
              icon={<Icon icon="mdi:account" height={24} width={24} />}
            />
            {/* Admin button - only show if user is admin */}
            {isAdmin && (
              <IconButton
                {...colorSchemeProps('admin', 'purple')}
                aria-label={
                  viewMode === 'admin' ? 'Admin dashboard (current)' : 'Switch to admin dashboard'
                }
                aria-pressed={viewMode === 'admin'}
                onClick={() => setViewMode('admin')}
                size="md"
                flexShrink={0}
                icon={<Icon icon="mdi:cog" height={24} width={24} />}
              />
            )}
          </>
        )}

        {/* Address button - ensure consistent width */}
        <Box flexShrink={0} minW="120px" maxW="180px">
          <AddressButton
            address={address}
            isConnected={isConnected}
            isSignedIn={isSignedIn}
            onLogout={onLogout}
          />
        </Box>
      </Flex>
    </Box>
  )
}

interface NavigationBarProps {
  address: string | undefined
  isConnected: boolean
  isSignedIn: boolean
  onLogout?: () => void
  children: React.ReactNode
  renderBottomNav?: boolean
}

const NavigationBar = ({
  address,
  isConnected,
  isSignedIn,
  onLogout,
  children,
  renderBottomNav = true,
}: NavigationBarProps) => {
  const { viewMode, setViewMode } = useViewMode()
  const { colorMode, toggleColorMode } = useColorMode()
  const bgColor = 'bg.band'
  const borderColor = 'line.dark'
  const { projectId } = useProjects()
  const { data } = useSacrificeStats()
  const lightMode = useColorModeValue(true, false)
  // Check if user has admin role - only show if signed in and has admin role
  const isAdmin = isAdminRole(isSignedIn, data?.user?.role?.role)
  const { isInstanceAdmin } = useInstanceAdmin()

  const colorSchemeProps = useCallback(
    (matchingMode: string, variant: string) =>
      buildViewModeColorScheme({ matchingMode, viewMode, variant, lightMode }),
    [lightMode, viewMode]
  )

  return (
    <>
      {/* Top navigation bar - desktop only */}
      <Box
        as="nav"
        bg={bgColor}
        borderBottomWidth="1px"
        borderBottomStyle="solid"
        borderColor={borderColor}
        px={4}
        py={2}
        position="sticky"
        top={0}
        zIndex={1000}
        display={{ base: 'none', lg: 'block' }}
      >
        <Flex align="center" justify="space-between" maxW="container.xl" mx="auto" h="43px">
          {/* Desktop view - buttons on the left */}
          <HStack spacing={2}>
            {/* Home — the Altar mark, furthest left, returns to the landing page.
                The wordmark joins the mark only on wider (xl) screens. */}
            <HStack
              as={RouterLink}
              to="/"
              spacing={2}
              aria-label="Altar home"
              flexShrink={0}
              pr={1}
              _hover={{ opacity: 0.85 }}
            >
              <Image src="/altar-favicon.svg" alt="" boxSize="28px" />
              <Heading
                as="span"
                size="md"
                color="text.engraved"
                lineHeight="1"
                display={{ base: 'none', xl: 'block' }}
              >
                Altar
              </Heading>
            </HStack>
            <IconButton
              as={RouterLink}
              to="/sacrifices"
              aria-label="All campaigns"
              variant="ghost"
              size="md"
              p={2}
              icon={<Icon icon="mdi:view-grid" height={24} width={24} />}
            />
            <IconButton
              aria-label="Toggle color mode"
              onClick={toggleColorMode}
              variant="ghost"
              size="md"
              p={2}
              icon={
                <Icon
                  icon={colorMode === 'light' ? 'mdi:weather-night' : 'mdi:weather-sunny'}
                  height={24}
                  width={24}
                />
              }
            />
            <SettingsButton />
            {/* Instance dashboard — only visible to instance admins */}
            {isInstanceAdmin && (
              <IconButton
                as={RouterLink}
                to="/instance"
                aria-label="Instance"
                variant="ghost"
                size="md"
                p={2}
                icon={<Icon icon="mdi:shield-account" height={24} width={24} />}
              />
            )}
            {projectId ? (
              <ButtonGroup isAttached>
                <Button
                  {...colorSchemeProps('global', 'green')}
                  border="1px solid"
                  px={0}
                  onClick={() => setViewMode('global')}
                  borderColor={borderColor}
                  size="sm"
                  width={10}
                  height={10}
                  aria-label={
                    viewMode === 'global' ? 'Global view (current)' : 'Switch to global view'
                  }
                  aria-pressed={viewMode === 'global'}
                >
                  <Icon icon="game-icons:rank-3" height={24} width={24} />
                </Button>
                <Button
                  {...colorSchemeProps('user', 'green')}
                  mx="-1px"
                  border="1px solid"
                  px={0}
                  onClick={() => setViewMode('user')}
                  borderColor={borderColor}
                  size="sm"
                  width={10}
                  height={10}
                  aria-label={viewMode === 'user' ? 'User view (current)' : 'Switch to user view'}
                  aria-pressed={viewMode === 'user'}
                >
                  <Icon icon="mdi:account" height={24} width={24} />
                </Button>
                {isAdmin && (
                  <Button
                    {...colorSchemeProps('admin', 'purple')}
                    border="1px solid"
                    px={0}
                    onClick={() => setViewMode('admin')}
                    borderColor={borderColor}
                    size="sm"
                    width={10}
                    height={10}
                    aria-label={
                      viewMode === 'admin'
                        ? 'Admin dashboard (current)'
                        : 'Switch to admin dashboard'
                    }
                    aria-pressed={viewMode === 'admin'}
                  >
                    <Icon icon="mdi:cog" height={24} width={24} />
                  </Button>
                )}
              </ButtonGroup>
            ) : (
              <></>
            )}
          </HStack>

          {/* Address button on the right - desktop only */}
          {/* AddressButton handles its own lazy loading internally - no wrapper needed */}
          <AddressButton
            address={address}
            isConnected={isConnected}
            isSignedIn={isSignedIn}
            onLogout={onLogout}
          />
        </Flex>
      </Box>

      {/* Main content */}
      {children}

      {/* Bottom navigation bar - mobile only, positioned at bottom of viewport */}
      {renderBottomNav && (
        <BottomNav
          address={address}
          isConnected={isConnected}
          isSignedIn={isSignedIn}
          onLogout={onLogout}
        />
      )}
    </>
  )
}

export default NavigationBar
